/**
 * Build / edit one saved WhatsApp Interactive Message (List or Button).
 *
 * The sections/rows and buttons editors here are the same editing UX
 * WorkflowCanvas.tsx's SendListMessageConfig/SendButtonMessageConfig used
 * before those became simple pickers — moved here because a 10-row menu
 * builder needs a full-width page with a live preview, not a cramped node
 * side-panel.
 *
 * `{{n}}` in body/header/footer is intentional authoring syntax, not a typo —
 * see whatsapp-interactive-message.util.ts on the backend for the substitution
 * rule (Twilio-style, positional, sequential from 1). Row/button titles never
 * take variables: those are the routing contract, so only display text varies
 * per send.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import {
  Box, Typography, TextField, MenuItem, Button, IconButton, Stack,
  CircularProgress, Alert, Divider, FormControlLabel, Switch, Paper,
  ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import { Add, Delete, ArrowBack, Save } from '@mui/icons-material';
import adminApi, {
  InteractiveMessageInput,
  InteractiveMessageKind,
  InteractiveMessageBodyMode,
  InteractiveMessageMediaHeader,
  MediaAsset,
  INTERACTIVE_MESSAGE_LIMITS as LIMITS,
} from '@/services/admin.api';
import WhatsAppPreviewBubble from '../campaigns/WhatsAppPreviewBubble';

const extractErr = (e: any): string =>
  e?.response?.data?.error?.message || e?.message || 'Something went wrong';

/** Distinct `{{n}}` slot numbers already present in a text — same rule the
 *  backend's countInteractiveMessageVariables enforces. Used only to pick the
 *  next number for "+ Add variable", same convention WhatsAppTemplateEditor
 *  uses for its own body field. */
function extractVariableNumbers(text: string): number[] {
  const found = new Set<number>();
  const re = /\{\{\s*(\d+)\s*\}\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text || ''))) found.add(parseInt(m[1] as string, 10));
  return [...found];
}

/** Inserts `snippet` at the real cursor position (falling back to the end),
 *  then restores focus and caret — same mechanism WhatsAppTemplateEditor's
 *  own "+ Add variable" button uses for its body field. */
function insertAtCursor(
  ref: React.RefObject<HTMLTextAreaElement>,
  currentValue: string,
  setValue: (next: string) => void,
  snippet: string
): void {
  const el = ref.current;
  const start = el?.selectionStart ?? currentValue.length;
  const end = el?.selectionEnd ?? currentValue.length;
  setValue(currentValue.slice(0, start) + snippet + currentValue.slice(end));
  requestAnimationFrame(() => {
    if (!el) return;
    el.focus();
    const pos = start + snippet.length;
    el.setSelectionRange(pos, pos);
  });
}

interface EditableRow {
  /** Present only once the server has minted it (an existing row). Echoed
   *  back on save so the id survives — see the service's own doc comment on
   *  why regenerating it would break a wired trigger branch. */
  id?: string | undefined;
  title: string;
  description: string;
}
interface EditableSection {
  title: string;
  rows: EditableRow[];
}
interface EditableButton {
  id?: string | undefined;
  title: string;
}

function emptySection(): EditableSection {
  return { title: '', rows: [{ title: '', description: '' }] };
}

const InteractiveMessageEditor: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isNew = !id || id === 'new';
  const pathPrefix = location.pathname.startsWith('/admin') ? '/admin' : '';

  const [kind, setKind] = useState<InteractiveMessageKind>(
    (searchParams.get('kind') as InteractiveMessageKind) || 'list'
  );
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [aiEnabled, setAiEnabled] = useState(false);
  const [bodyMode, setBodyMode] = useState<InteractiveMessageBodyMode>('variable');
  const [bodyText, setBodyText] = useState('');
  const bodyFieldRef = useRef<HTMLTextAreaElement>(null);
  const [headerText, setHeaderText] = useState('');
  const [footerText, setFooterText] = useState('');
  const [buttonText, setButtonText] = useState('');
  const [sections, setSections] = useState<EditableSection[]>([emptySection()]);
  const [buttons, setButtons] = useState<EditableButton[]>([{ title: '' }]);
  const [headerKind, setHeaderKind] = useState<'none' | 'media'>('none');
  const [headerMediaType, setHeaderMediaType] = useState<'image' | 'video' | 'document'>('document');
  const [headerMediaAssetId, setHeaderMediaAssetId] = useState('');
  const [mediaAssets, setMediaAssets] = useState<MediaAsset[]>([]);

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    adminApi.getMediaAssets().then(setMediaAssets).catch(() => setMediaAssets([]));
  }, []);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        setLoading(true);
        const m = await adminApi.getInteractiveMessage(id!);
        setKind(m.kind);
        setName(m.name);
        setDescription(m.description || '');
        setAiEnabled(m.aiEnabled);
        setBodyMode(m.bodyMode || 'variable');
        setBodyText(m.bodyText);
        setHeaderText(m.headerText || '');
        setFooterText(m.footerText || '');
        setButtonText(m.buttonText || '');
        if (m.kind === 'list') {
          setSections(
            (m.sections?.length ? m.sections : [{ rows: [{ title: '' }] }]).map((s) => ({
              title: s.title || '',
              rows: s.rows.map((r) => ({ id: r.id, title: r.title, description: r.description || '' })),
            }))
          );
        } else {
          setButtons(
            (m.buttons?.length ? m.buttons : [{ title: '' }]).map((b) => ({ id: b.id, title: b.title }))
          );
          if (m.header) {
            setHeaderKind('media');
            setHeaderMediaType(m.header.type);
            setHeaderMediaAssetId(m.header.mediaAssetId);
          }
        }
      } catch (e) {
        setError(extractErr(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isNew]);

  const totalRows = sections.reduce((n, s) => n + s.rows.length, 0);

  const addSection = () => {
    if (sections.length >= LIMITS.listMaxSections) return;
    setSections([...sections, emptySection()]);
  };
  const removeSection = (i: number) => setSections(sections.filter((_, idx) => idx !== i));
  const updateSection = (i: number, patch: Partial<EditableSection>) =>
    setSections(sections.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const addRow = (sIdx: number) => {
    if (totalRows >= LIMITS.listMaxRowsTotal) return;
    updateSection(sIdx, { rows: [...sections[sIdx]!.rows, { title: '', description: '' }] });
  };
  const updateRow = (sIdx: number, rIdx: number, patch: Partial<EditableRow>) =>
    updateSection(sIdx, {
      rows: sections[sIdx]!.rows.map((r, idx) => (idx === rIdx ? { ...r, ...patch } : r)),
    });
  const removeRow = (sIdx: number, rIdx: number) =>
    updateSection(sIdx, { rows: sections[sIdx]!.rows.filter((_, idx) => idx !== rIdx) });

  const addButton = () => {
    if (buttons.length >= LIMITS.replyButtonMax) return;
    setButtons([...buttons, { title: '' }]);
  };
  const updateButton = (i: number, patch: Partial<EditableButton>) =>
    setButtons(buttons.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));
  const removeButton = (i: number) => setButtons(buttons.filter((_, idx) => idx !== i));

  const selectedAsset = useMemo(
    () => mediaAssets.find((m) => m._id === headerMediaAssetId),
    [mediaAssets, headerMediaAssetId]
  );

  const handleSave = async () => {
    setError('');
    if (!name.trim()) {
      setError('A name is required.');
      return;
    }
    if (!bodyText.trim()) {
      setError(bodyMode === 'dynamic' ? 'Guidance for the AI is required.' : 'Body text is required.');
      return;
    }

    const header: InteractiveMessageMediaHeader | undefined =
      kind === 'button' && headerKind === 'media' && headerMediaAssetId
        ? { type: headerMediaType, mediaAssetId: headerMediaAssetId }
        : undefined;

    const input: InteractiveMessageInput = {
      kind,
      name: name.trim(),
      description: description.trim() || undefined,
      // Dynamic mode requires AI access — nothing else can produce free text —
      // so the toggle is force-enabled and locked while this mode is selected
      // (see the mode ToggleButtonGroup below); this is the actual source of
      // truth sent to the server either way.
      aiEnabled: bodyMode === 'dynamic' ? true : aiEnabled,
      bodyMode,
      bodyText: bodyText.trim(),
      headerText: kind === 'button' && header ? undefined : headerText.trim() || undefined,
      footerText: footerText.trim() || undefined,
      ...(kind === 'list'
        ? {
            buttonText: buttonText.trim(),
            sections: sections.map((s) => ({
              title: s.title.trim() || undefined,
              rows: s.rows.map((r) => ({
                ...(r.id ? { id: r.id } : {}),
                title: r.title.trim(),
                description: r.description.trim() || undefined,
              })),
            })),
          }
        : {
            buttons: buttons.map((b) => ({ ...(b.id ? { id: b.id } : {}), title: b.title.trim() })),
            ...(header ? { header } : {}),
          }),
    };

    try {
      setSaving(true);
      if (isNew) {
        await adminApi.createInteractiveMessage(input);
      } else {
        await adminApi.updateInteractiveMessage(id!, input);
      }
      navigate(`${pathPrefix}/whatsapp-interactive-messages`);
    } catch (e) {
      setError(extractErr(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <IconButton onClick={() => navigate(`${pathPrefix}/whatsapp-interactive-messages`)}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h5" fontWeight="bold">
          {isNew ? `New ${kind === 'list' ? 'List' : 'Message Draft'}` : name || 'Edit message'}
        </Typography>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box display="flex" gap={3} flexWrap="wrap">
        <Stack spacing={2} sx={{ flex: '1 1 480px', minWidth: 360 }}>
          <TextField
            label="Name"
            size="small"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Package menu"
          />
          <TextField
            label="When should the AI use this?"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. When a customer asks what tour packages are available"
            helperText="This is the only signal an AI agent gets when picking between saved messages — be specific."
          />
          <FormControlLabel
            control={
              <Switch
                checked={bodyMode === 'dynamic' ? true : aiEnabled}
                disabled={bodyMode === 'dynamic'}
                onChange={(e) => setAiEnabled(e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body2">Let AI agents send this message</Typography>
                <Typography variant="caption" color="text.secondary">
                  {bodyMode === 'dynamic'
                    ? 'Required and locked on — a fully-dynamic body only makes sense when an AI is writing it.'
                    : "Off by default. The AI only ever sees messages you've explicitly enabled here."}
                </Typography>
              </Box>
            }
          />

          <Divider />

          <Box>
            <Typography variant="body2" fontWeight={600} mb={0.5}>
              Body content
            </Typography>
            <ToggleButtonGroup
              value={bodyMode}
              exclusive
              size="small"
              disabled={!isNew}
              onChange={(_e, next: InteractiveMessageBodyMode | null) => {
                if (!next) return;
                setBodyMode(next);
                if (next === 'dynamic') setAiEnabled(true);
              }}
              sx={{ mb: 1 }}
            >
              <ToggleButton value="variable" sx={{ textTransform: 'none' }}>
                Fixed text + variables
              </ToggleButton>
              <ToggleButton value="dynamic" sx={{ textTransform: 'none' }}>
                Fully dynamic (AI writes it)
              </ToggleButton>
            </ToggleButtonGroup>
            {!isNew && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Locked after creation — switching modes changes what this field means (real
                wording vs. AI guidance) and could silently break anything already wired to it.
                Create a new message instead if you need the other mode.
              </Typography>
            )}
            <Alert severity="info" sx={{ fontSize: 13 }}>
              {bodyMode === 'variable'
                ? 'The wording below is exactly what gets sent, with {{n}} slots filled per send. A workflow node or the AI can supply the values.'
                : "The wording below is never sent — it's guidance the AI follows to write the body itself, in its own words, each time. This message sends as a SINGLE bubble: the AI should not also send a separate reply repeating it. Dynamic messages are AI-only and won't appear in the workflow canvas's picker."}
            </Alert>
          </Box>

          <TextField
            label={bodyMode === 'dynamic' ? 'Guidance for the AI' : 'Body text'}
            size="small"
            fullWidth
            multiline
            minRows={2}
            inputRef={bodyFieldRef}
            value={bodyText}
            onChange={(e) => setBodyText(e.target.value)}
            {...(bodyMode === 'dynamic'
              ? {
                  placeholder:
                    'e.g. Write a short, warm 1-2 sentence intro to the menu, based on what the customer just said.',
                }
              : {})}
            helperText={
              bodyMode === 'dynamic'
                ? 'Describe what the body should communicate — the AI writes the actual wording at send time.'
                : 'Use {{1}}, {{2}}… for values that vary per send, e.g. "Packages for {{1}}"'
            }
          />
          {bodyMode === 'variable' && (
            <Button
              size="small"
              sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 600 }}
              onClick={() => {
                const next = extractVariableNumbers(bodyText).length + 1;
                insertAtCursor(bodyFieldRef, bodyText, setBodyText, `{{${next}}}`);
              }}
            >
              + Add variable
            </Button>
          )}
          <TextField
            label="Header text (optional)"
            size="small"
            fullWidth
            disabled={kind === 'button' && headerKind === 'media'}
            value={headerText}
            onChange={(e) => setHeaderText(e.target.value)}
          />
          <TextField
            label="Footer text (optional)"
            size="small"
            fullWidth
            value={footerText}
            onChange={(e) => setFooterText(e.target.value)}
          />

          {kind === 'list' && (
            <TextField
              label="List button label"
              size="small"
              fullWidth
              value={buttonText}
              onChange={(e) => setButtonText(e.target.value.slice(0, LIMITS.listButtonTextMax))}
              helperText={`${buttonText.length}/${LIMITS.listButtonTextMax} — the button that opens the menu (e.g. "Menu")`}
              inputProps={{ maxLength: LIMITS.listButtonTextMax }}
            />
          )}

          {kind === 'button' && (
            <>
              <TextField
                select
                label="Header"
                size="small"
                fullWidth
                value={headerKind}
                onChange={(e) => {
                  const next = e.target.value as 'none' | 'media';
                  setHeaderKind(next);
                  // Media and text headers are mutually exclusive on the saved
                  // record (see IInteractiveMessageMediaHeader on the backend) —
                  // clear whichever one the author just switched away from so a
                  // leftover value can never be saved as the OTHER kind of header.
                  if (next === 'media') setHeaderText('');
                  else setHeaderMediaAssetId('');
                }}
              >
                <MenuItem value="none">Text only (use the field above)</MenuItem>
                <MenuItem value="media">File (image / video / document)</MenuItem>
              </TextField>
              {headerKind === 'media' && (
                <>
                  <TextField
                    select
                    label="Header media type"
                    size="small"
                    fullWidth
                    value={headerMediaType}
                    onChange={(e) => setHeaderMediaType(e.target.value as any)}
                  >
                    <MenuItem value="document">Document</MenuItem>
                    <MenuItem value="image">Image</MenuItem>
                    <MenuItem value="video">Video</MenuItem>
                  </TextField>
                  <TextField
                    select
                    label="File"
                    size="small"
                    fullWidth
                    value={headerMediaAssetId}
                    onChange={(e) => setHeaderMediaAssetId(e.target.value)}
                    helperText={
                      mediaAssets.filter((m) => m.type === headerMediaType).length === 0
                        ? 'No matching media yet — upload files under Sources → Media first.'
                        : undefined
                    }
                  >
                    {mediaAssets
                      .filter((m) => m.type === headerMediaType)
                      .map((m) => (
                        <MenuItem key={m._id} value={m._id}>
                          {m.name}
                        </MenuItem>
                      ))}
                  </TextField>
                </>
              )}
            </>
          )}

          <Divider />

          {kind === 'list' ? (
            <>
              <Typography variant="subtitle2">
                Sections & options ({totalRows}/{LIMITS.listMaxRowsTotal} rows)
              </Typography>
              {sections.map((section, sIdx) => (
                <Paper key={sIdx} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <TextField
                      label="Section title (optional)"
                      size="small"
                      fullWidth
                      value={section.title}
                      onChange={(e) => updateSection(sIdx, { title: e.target.value })}
                    />
                    {sections.length > 1 && (
                      <IconButton size="small" onClick={() => removeSection(sIdx)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                  {section.rows.map((row, rIdx) => (
                    <Stack key={rIdx} direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1 }}>
                      <TextField
                        label="Option"
                        size="small"
                        value={row.title}
                        onChange={(e) =>
                          updateRow(sIdx, rIdx, { title: e.target.value.slice(0, LIMITS.rowTitleMax) })
                        }
                        helperText={`${row.title.length}/${LIMITS.rowTitleMax}`}
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        label="Description (optional)"
                        size="small"
                        value={row.description}
                        onChange={(e) =>
                          updateRow(sIdx, rIdx, {
                            description: e.target.value.slice(0, LIMITS.rowDescriptionMax),
                          })
                        }
                        helperText={`${row.description.length}/${LIMITS.rowDescriptionMax}`}
                        sx={{ flex: 1 }}
                      />
                      <IconButton size="small" onClick={() => removeRow(sIdx, rIdx)} sx={{ mt: 0.5 }}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}
                  <Button
                    size="small"
                    startIcon={<Add />}
                    onClick={() => addRow(sIdx)}
                    disabled={totalRows >= LIMITS.listMaxRowsTotal}
                  >
                    Add option
                  </Button>
                </Paper>
              ))}
              <Button
                size="small"
                startIcon={<Add />}
                onClick={addSection}
                disabled={sections.length >= LIMITS.listMaxSections}
              >
                Add section
              </Button>
            </>
          ) : (
            <>
              <Typography variant="subtitle2">
                Reply buttons ({buttons.length}/{LIMITS.replyButtonMax})
              </Typography>
              {buttons.map((b, i) => (
                <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
                  <TextField
                    label={`Button ${i + 1}`}
                    size="small"
                    fullWidth
                    value={b.title}
                    onChange={(e) =>
                      updateButton(i, { title: e.target.value.slice(0, LIMITS.replyButtonTitleMax) })
                    }
                    helperText={`${b.title.length}/${LIMITS.replyButtonTitleMax}`}
                  />
                  <IconButton size="small" onClick={() => removeButton(i)} sx={{ mt: 0.5 }}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
              <Button
                size="small"
                startIcon={<Add />}
                onClick={addButton}
                disabled={buttons.length >= LIMITS.replyButtonMax}
              >
                Add button
              </Button>
            </>
          )}

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button onClick={() => navigate(`${pathPrefix}/whatsapp-interactive-messages`)}>Cancel</Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} /> : <Save />}
              onClick={handleSave}
              disabled={saving}
            >
              Save
            </Button>
          </Stack>
        </Stack>

        <Box sx={{ flex: '0 1 360px', position: 'sticky', top: 16, alignSelf: 'flex-start' }}>
          <Typography variant="subtitle2" mb={1} color="text.secondary">
            Preview
          </Typography>
          {bodyMode === 'dynamic' && (
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              The body below is a placeholder — the AI writes the real wording at send time.
            </Typography>
          )}
          {kind === 'list' ? (
            <WhatsAppPreviewBubble
              headerText={headerText || undefined}
              bodyText={bodyMode === 'dynamic' ? '[Written by the AI at send time]' : bodyText}
              footerText={footerText || undefined}
              emptyHint="Your message preview will appear here…"
              list={{
                buttonText: buttonText || 'Menu',
                sections: sections.map((s) => ({
                  title: s.title || undefined,
                  rows: s.rows.map((r) => ({ title: r.title, description: r.description || undefined })),
                })),
              }}
            />
          ) : (
            <WhatsAppPreviewBubble
              mediaType={
                headerKind === 'media' ? (headerMediaType.toUpperCase() as 'IMAGE' | 'VIDEO' | 'DOCUMENT') : undefined
              }
              mediaUrl={headerKind === 'media' ? selectedAsset?.s3Url ?? null : null}
              mediaFileName={headerKind === 'media' ? selectedAsset?.originalName : undefined}
              headerText={headerKind === 'none' ? headerText || undefined : undefined}
              bodyText={bodyMode === 'dynamic' ? '[Written by the AI at send time]' : bodyText}
              footerText={footerText || undefined}
              emptyHint="Your message preview will appear here…"
              buttons={buttons.map((b) => ({ type: 'QUICK_REPLY', text: b.title || 'Button' }))}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default InteractiveMessageEditor;
